import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Home, DollarSign, TrendingUp, AlertCircle, CheckCircle, Info, Calculator, Save, Edit2 } from 'lucide-react';
import { toast } from 'sonner';



const HELOCAnalysis = ({ mortgage }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showHELOCRequest, setShowHELOCRequest] = useState(false);
  const [helocRequest, setHelocRequest] = useState({
    desired_amount: '',
    purpose: 'home_renovation',
    repayment_plan: 'interest_only'
  });
  const [savedHELOC, setSavedHELOC] = useState(null);
  const [validationError, setValidationError] = useState('');
  
  const [calcValues, setCalcValues] = useState({
    home_value: mortgage?.home_value || '',
    current_balance: mortgage?.current_balance || '',
    primary_residence: mortgage?.primary_residence || 'yes'
  });
  
  useEffect(() => {
    if (mortgage?.home_value && mortgage?.current_balance) {
      fetchAnalysis();
      fetchSavedHELOC();
    }
  }, [mortgage]);
  
  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/heloc-analysis`);
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error fetching HELOC analysis:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load HELOC analysis');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSavedHELOC = async () => {
    try {
      const response = await api.get(`/api/heloc-request`);
      if (response.data) {
        setSavedHELOC(response.data);
        setHelocRequest({
          desired_amount: response.data.desired_amount || '',
          purpose: response.data.purpose || 'home_renovation',
          repayment_plan: response.data.repayment_plan || 'interest_only'
        });
      }
    } catch (error) {
      // No saved HELOC request, that's fine
      console.log('No saved HELOC request');
    }
  };
  
  const validateAmount = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return 'Please enter a valid amount greater than $0';
    }
    if (analysis && numAmount > analysis.max_heloc_available) {
      return `Amount exceeds maximum available (${formatCurrency(analysis.max_heloc_available)}). Your equity only supports up to this amount at ${analysis.combined_ltv_limit}% combined LTV.`;
    }
    if (numAmount < 10000) {
      return 'Minimum HELOC amount is typically $10,000';
    }
    return '';
  };
  
  const handleAmountChange = (value) => {
    setHelocRequest({ ...helocRequest, desired_amount: value });
    if (value) {
      const error = validateAmount(value);
      setValidationError(error);
    } else {
      setValidationError('');
    }
  };
  
  const saveHELOCRequest = async () => {
    const error = validateAmount(helocRequest.desired_amount);
    if (error) {
      setValidationError(error);
      toast.error(error);
      return;
    }
    
    try {
      setSaving(true);
      const response = await api.post(`/api/heloc-request`, {
        desired_amount: parseFloat(helocRequest.desired_amount),
        purpose: helocRequest.purpose,
        repayment_plan: helocRequest.repayment_plan,
        max_available: analysis?.max_heloc_available || 0,
        heloc_rate: analysis?.heloc_rate_estimate || 0
      });
      
      setSavedHELOC(response.data);
      setShowHELOCRequest(false);
      toast.success('HELOC request saved successfully!');
    } catch (error) {
      console.error('Error saving HELOC request:', error);
      toast.error(error.response?.data?.detail || 'Failed to save HELOC request');
    } finally {
      setSaving(false);
    }
  };
  
  const calculateScenario = async () => {
    if (!calcValues.home_value || !calcValues.current_balance) {
      toast.error('Please enter home value and current balance');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.post(
        `/api/heloc-analysis/calculate`,
        null,
        {
          params: {
            home_value: parseFloat(calcValues.home_value),
            current_balance: parseFloat(calcValues.current_balance),
            primary_residence: calcValues.primary_residence
          }
        }
      );
      setAnalysis(response.data);
      toast.success('HELOC analysis calculated');
    } catch (error) {
      console.error('Error calculating HELOC:', error);
      toast.error('Failed to calculate HELOC');
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(value);
  };
  
  const calculateMonthlyPayment = (amount, rate) => {
    return (amount * (rate / 100)) / 12;
  };
  
  const PURPOSE_OPTIONS = [
    { value: 'home_renovation', label: 'Home Renovation' },
    { value: 'debt_consolidation', label: 'Debt Consolidation' },
    { value: 'investment', label: 'Investment (Smith Maneuver)' },
    { value: 'emergency_fund', label: 'Emergency Fund Access' },
    { value: 'education', label: 'Education Expenses' },
    { value: 'other', label: 'Other' }
  ];
  
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div data-testid="heloc-analysis" className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-heading font-semibold flex items-center gap-2">
          <Home className="h-6 w-6 text-primary" />
          HELOC Analysis
        </h3>
        <button
          onClick={() => setShowCalculator(!showCalculator)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-muted hover:bg-muted/80 rounded-lg transition-colors"
        >
          <Calculator className="h-4 w-4" />
          {showCalculator ? 'Hide Calculator' : 'Try Calculator'}
        </button>
      </div>
      
      {/* Calculator */}
      {showCalculator && (
        <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-4">
          <h4 className="text-sm font-semibold">HELOC Scenario Calculator</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Home Value ($)</label>
              <input
                type="number"
                value={calcValues.home_value}
                onChange={(e) => setCalcValues({ ...calcValues, home_value: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                placeholder="600000"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Current Mortgage ($)</label>
              <input
                type="number"
                value={calcValues.current_balance}
                onChange={(e) => setCalcValues({ ...calcValues, current_balance: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                placeholder="400000"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Property Type</label>
              <select
                value={calcValues.primary_residence}
                onChange={(e) => setCalcValues({ ...calcValues, primary_residence: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
              >
                <option value="yes">Primary Residence</option>
                <option value="no">Investment Property</option>
              </select>
            </div>
          </div>
          <button
            onClick={calculateScenario}
            disabled={loading}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm"
          >
            Calculate HELOC
          </button>
        </div>
      )}
      
      {/* Analysis Results */}
      {analysis ? (
        <div className="space-y-6">
          {/* Eligibility Banner */}
          <div className={`flex items-center gap-3 p-4 rounded-xl ${
            analysis.eligible 
              ? 'bg-green-500/10 border border-green-500/20' 
              : 'bg-yellow-500/10 border border-yellow-500/20'
          }`}>
            {analysis.eligible ? (
              <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-6 w-6 text-yellow-400 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className={`font-semibold ${analysis.eligible ? 'text-green-400' : 'text-yellow-400'}`}>
                {analysis.eligible ? 'You May Qualify for a HELOC' : 'HELOC Eligibility Limited'}
              </p>
              <p className="text-sm text-muted-foreground">
                {analysis.eligible 
                  ? `Up to ${formatCurrency(analysis.max_heloc_available)} available based on your equity`
                  : `Your current LTV of ${analysis.ltv_ratio.toFixed(1)}% limits HELOC options`
                }
              </p>
            </div>
            {analysis.eligible && !showHELOCRequest && (
              <button
                onClick={() => setShowHELOCRequest(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm"
              >
                <Edit2 className="h-4 w-4" />
                {savedHELOC ? 'Edit Request' : 'Set HELOC Amount'}
              </button>
            )}
          </div>
          
          {/* HELOC Request Form */}
          {showHELOCRequest && analysis.eligible && (
            <div className="bg-card border-2 border-primary/30 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  Set Your HELOC Request
                </h4>
                <button
                  onClick={() => setShowHELOCRequest(false)}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Desired HELOC Amount
                    <span className="text-muted-foreground ml-2 text-xs">
                      (Max: {formatCurrency(analysis.max_heloc_available)})
                    </span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <input
                      type="number"
                      value={helocRequest.desired_amount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      className={`w-full pl-8 pr-3 py-2.5 bg-background border rounded-lg focus:ring-2 focus:ring-primary ${
                        validationError ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="50000"
                      max={analysis.max_heloc_available}
                    />
                  </div>
                  {validationError && (
                    <p className="mt-1.5 text-sm text-red-400 flex items-start gap-1">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      {validationError}
                    </p>
                  )}
                  {helocRequest.desired_amount && !validationError && (
                    <p className="mt-1.5 text-sm text-green-400 flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      Est. monthly interest: {formatCurrency(calculateMonthlyPayment(parseFloat(helocRequest.desired_amount), analysis.heloc_rate_estimate))}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Purpose</label>
                  <select
                    value={helocRequest.purpose}
                    onChange={(e) => setHelocRequest({ ...helocRequest, purpose: e.target.value })}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                  >
                    {PURPOSE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Repayment Plan</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setHelocRequest({ ...helocRequest, repayment_plan: 'interest_only' })}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      helocRequest.repayment_plan === 'interest_only'
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <p className="font-medium">Interest Only</p>
                    <p className="text-xs text-muted-foreground">Pay only interest, flexible principal</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setHelocRequest({ ...helocRequest, repayment_plan: 'principal_interest' })}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      helocRequest.repayment_plan === 'principal_interest'
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <p className="font-medium">Principal + Interest</p>
                    <p className="text-xs text-muted-foreground">Fixed payments over time</p>
                  </button>
                </div>
              </div>
              
              {/* Amount Slider */}
              {analysis.max_heloc_available > 0 && (
                <div>
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>$10,000</span>
                    <span>{formatCurrency(analysis.max_heloc_available)}</span>
                  </div>
                  <input
                    type="range"
                    min="10000"
                    max={analysis.max_heloc_available}
                    step="5000"
                    value={helocRequest.desired_amount || 10000}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                </div>
              )}
              
              <button
                onClick={saveHELOCRequest}
                disabled={saving || !!validationError || !helocRequest.desired_amount}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save HELOC Request'}
              </button>
            </div>
          )}
          
          {/* Saved HELOC Display */}
          {savedHELOC && !showHELOCRequest && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  Your HELOC Request
                </h4>
                <button
                  onClick={() => setShowHELOCRequest(true)}
                  className="text-sm text-primary hover:underline"
                >
                  Edit
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Requested Amount</p>
                  <p className="font-bold font-mono text-lg">{formatCurrency(savedHELOC.desired_amount)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Purpose</p>
                  <p className="font-medium capitalize">{savedHELOC.purpose?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Est. Monthly Interest</p>
                  <p className="font-bold font-mono">{formatCurrency(calculateMonthlyPayment(savedHELOC.desired_amount, savedHELOC.heloc_rate || analysis?.heloc_rate_estimate || 5))}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Repayment Plan</p>
                  <p className="font-medium capitalize">{savedHELOC.repayment_plan?.replace('_', ' ')}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <Home className="h-4 w-4 text-blue-400" />
                </div>
                <p className="text-sm text-muted-foreground">Home Equity</p>
              </div>
              <p className="text-2xl font-bold font-mono">{formatCurrency(analysis.equity)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {((analysis.equity / analysis.home_value) * 100).toFixed(1)}% of home value
              </p>
            </div>
            
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">Max HELOC</p>
              </div>
              <p className="text-2xl font-bold font-mono text-primary">{formatCurrency(analysis.max_heloc_available)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                At {analysis.combined_ltv_limit}% combined LTV
              </p>
            </div>
            
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-purple-400" />
                </div>
                <p className="text-sm text-muted-foreground">Est. Rate</p>
              </div>
              <p className="text-2xl font-bold font-mono">{analysis.heloc_rate_estimate}%</p>
              <p className="text-xs text-muted-foreground mt-1">Prime + 0.5%</p>
            </div>
            
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 bg-orange-500/10 rounded-lg flex items-center justify-center">
                  <Calculator className="h-4 w-4 text-orange-400" />
                </div>
                <p className="text-sm text-muted-foreground">Interest Only</p>
              </div>
              <p className="text-2xl font-bold font-mono">{formatCurrency(analysis.monthly_interest_only)}</p>
              <p className="text-xs text-muted-foreground mt-1">Monthly on max HELOC</p>
            </div>
          </div>
          
          {/* LTV Gauge */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h4 className="text-sm font-semibold mb-4">Loan-to-Value (LTV) Status</h4>
            <div className="relative h-4 bg-muted rounded-full overflow-hidden">
              <div 
                className={`absolute h-full rounded-full transition-all ${
                  analysis.ltv_ratio <= 65 ? 'bg-green-500' :
                  analysis.ltv_ratio <= 75 ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${Math.min(analysis.ltv_ratio, 100)}%` }}
              />
              {/* Threshold markers */}
              <div className="absolute top-0 h-full w-0.5 bg-white/50" style={{ left: '65%' }} />
              <div className="absolute top-0 h-full w-0.5 bg-white/50" style={{ left: '80%' }} />
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>0%</span>
              <span>65% (Inv.)</span>
              <span>80% (Primary)</span>
              <span>100%</span>
            </div>
            <p className="text-center mt-3 text-sm">
              Your LTV: <span className="font-bold font-mono">{analysis.ltv_ratio.toFixed(1)}%</span>
            </p>
          </div>
          
          {/* Recommendations */}
          {analysis.recommendations && analysis.recommendations.length > 0 && (
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Info className="h-4 w-4 text-primary" />
                Recommendations
              </h4>
              <ul className="space-y-2">
                {analysis.recommendations.map((rec) => (
                  <li key={rec} className="flex items-start gap-2 text-sm">
                    <span className="text-primary mt-0.5">•</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* HELOC Uses */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h4 className="text-sm font-semibold mb-4">Common HELOC Uses</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <p className="font-medium">Home Renovations</p>
                <p className="text-xs text-muted-foreground mt-1">Increase property value</p>
              </div>
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <p className="font-medium">Debt Consolidation</p>
                <p className="text-xs text-muted-foreground mt-1">Lower interest costs</p>
              </div>
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <p className="font-medium">Investment</p>
                <p className="text-xs text-muted-foreground mt-1">Smith Maneuver</p>
              </div>
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <p className="font-medium">Emergency Fund</p>
                <p className="text-xs text-muted-foreground mt-1">Access when needed</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <Home className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">
            Save your mortgage details to see your HELOC analysis, or use the calculator above.
          </p>
          <button
            onClick={() => setShowCalculator(true)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Try the Calculator
          </button>
        </div>
      )}
    </div>
  );
};

export default HELOCAnalysis;
